import * as Yup from 'yup'
import { IComponentMaterialRow, IPackagingProcessingActivityOption } from './types'

export const EI_NAME_MAX_LENGTH = 512

// Exact Match or Proxy is a nullable boolean, so it is a select and not a toggle: a toggle cannot
// express "not stated". Option id 0 is deliberately unused - it collides with the Select's
// noLabel placeholder value, which is why a Yes/No select is banned for non-nullable booleans.
export const EXACT_MATCH_OPTIONS = [
  { label: 'Exact match', value: 1 },
  { label: 'Proxy', value: 2 },
]

/**
 * Dropdown label for a packaging processing activity: its code and its process flow, because a
 * processing activity has no name of its own and two activities can share a process flow. Falls
 * back to whichever half is present.
 */
export const formatProcessingActivityLabel = (pa: IPackagingProcessingActivityOption): string => {
  const processFlow = pa.name ?? ''
  if (!pa.code) {
    return processFlow
  }
  return processFlow ? `${pa.code} - ${processFlow}` : pa.code
}

export const toNumberOrNull = (value: unknown): number | null =>
  value === '' || value === null || value === undefined || Number(value) < 0 ? null : Number(value)

/** Select value (1 exact, 2 proxy, -1 unset) to the stored tri-state boolean. */
export const toExactMatchValue = (value: unknown): boolean | null => {
  const option = toNumberOrNull(value)
  return option === null ? null : option === 1
}

/** Stored tri-state boolean to the select's option id. */
export const fromExactMatchValue = (isExactMatch: boolean | null): number =>
  isExactMatch === null || isExactMatch === undefined ? -1 : isExactMatch ? 1 : 2

const optionalRecycledContent = Yup.number()
  .nullable()
  .transform((value, original) => (original === '' || original === null ? null : value))
  .typeError('Default recycled content must be a number')
  .min(0, 'Default recycled content must be between 0 and 100')
  .max(100, 'Default recycled content must be between 0 and 100')

export const formSchema = Yup.object().shape({
  alias: Yup.string().nullable(true),
  madeOfMaterials: Yup.array().of(
    Yup.object().shape({
      defaultProcessingActivityId: Yup.number().nullable(true),
      defaultRecycledContent: optionalRecycledContent,
      eiName: Yup.string()
        .nullable(true)
        .max(EI_NAME_MAX_LENGTH, `EI name must be ${EI_NAME_MAX_LENGTH} characters or fewer`),
      isExactMatch: Yup.boolean().nullable(true),
    })
  ),
  name: Yup.string().required('Component name is required'),
})

// The dialog carries the same four combination fields as an expanded row, and validates them
// identically - a value rejected here must not become acceptable once the row exists.
export const materialModalSchema = Yup.object().shape({
  categoryId: Yup.number().min(1, 'Category is required').required('Category is required'),
  defaultRecycledContent: optionalRecycledContent,
  eiName: Yup.string()
    .nullable(true)
    .max(EI_NAME_MAX_LENGTH, `EI name must be ${EI_NAME_MAX_LENGTH} characters or fewer`),
  functionalUnitId: Yup.number().nullable(true),
})

export const validateMaterialRow = (
  categoryId: number,
  functionalUnitId: number | null,
  existingMaterials: IComponentMaterialRow[]
): string | null => {
  if (functionalUnitId === null) {
    if (
      existingMaterials.some(
        m =>
          m.packagingMaterialCategoryId === categoryId &&
          m.packagingMaterialFunctionalUnitId === null
      )
    ) {
      return 'This category is already covered for all materials.'
    }
    if (existingMaterials.some(m => m.packagingMaterialCategoryId === categoryId)) {
      return 'A specific material from this category is already selected. Adding a category-wide rule is redundant.'
    }
  } else {
    if (existingMaterials.some(m => m.packagingMaterialFunctionalUnitId === functionalUnitId)) {
      return 'This material is already selected.'
    }
    if (
      existingMaterials.some(
        m =>
          m.packagingMaterialCategoryId === categoryId &&
          m.packagingMaterialFunctionalUnitId === null
      )
    ) {
      return 'This category is already covered for all materials. Adding a specific material is redundant.'
    }
  }
  return null
}
